import React from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { EcommerceEventList } from "../../models/Event";
import { setupStyleMapFromStyleMap } from "../../utils/helpers";
import config from "../../config";
import { useCartStore } from "../../stores/CartStore";
import { COOKIE_BOXOFFICE_KEY } from "../../stores/AuthStore";
import { tokenIsValid } from "../../utils/jwtHelpers";
import { ClientConfiguration } from "../../models/ClientConfiguration";
import TournamentRegistrationFlow from "../../components/tournament/TournamentRegistrationFlow";

type Props = {
    eventList: EcommerceEventList;
    clientConfig: ClientConfiguration | null;
};

export default function TournamentRegistration({
    eventList,
    clientConfig,
}: Props) {
    useCartStore(eventList.cartUrl);

    React.useEffect(() => {
        if (eventList.newStyleMap) {
            setupStyleMapFromStyleMap(eventList.newStyleMap);
        }
    }, []);

    const pageTitle = `Tournament Registration | AuthentiGATE`;

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="robots" content="noindex" />
                <meta
                    name="description"
                    content="Register for tournaments and esports competitions"
                />
            </Head>
            <GoogleReCaptchaProvider reCaptchaKey="6Lc1XW4kAAAAACMuJ96qI9fgbsm03ItN2ah-fIny">
                <TournamentRegistrationFlow
                    eventList={eventList}
                    clientConfig={clientConfig}
                />
            </GoogleReCaptchaProvider>
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    let data: any,
        clientConfig: any = null,
        error = false;

    const boxoffice = context.req.cookies[COOKIE_BOXOFFICE_KEY];
    let salesContext = "ECOMMERCE";

    if (
        boxoffice == "true" &&
        tokenIsValid(
            context.req.cookies[
                config.COOKIE_PREFIX + "__authentigate_token_sanitized"
            ],
        )
    ) {
        salesContext = "BOXOFFICE";
    }

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/ecommerce-events/CNE`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": salesContext,
                    Cookie: context.req.headers.cookie ?? "",
                },
                credentials: "include",
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            data = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    if (salesContext === "ECOMMERCE") {
        try {
            const res = await fetch(
                `${config.BACKEND_URI}/sales/client-configuration/CNE`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-Sales-Context": "ECOMMERCE",
                    },
                },
            );
            if (res.status !== 200) {
                error = true;
                console.log(await res.text());
            } else {
                clientConfig = await res.json();
            }
        } catch (err) {
            console.log(err);
            error = true;
        }
    }

    if (data?.newStyleMap) {
        setupStyleMapFromStyleMap(data.newStyleMap);
    }

    return {
        props: {
            eventList: data,
            clientConfig,
        },
        notFound: error,
    };
};
